import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useCountryArr } from "../../hooks/useCountryArr";
import { useSelector } from "react-redux";
import { selectCurrentRoles } from "../../store/slices/auth/authSlice";
import {
  isAllowedToSetAdminRights,
  isAllowedToSetApproverRights,
  isAllowedToSetUserRights,
} from "../../services/auth/Roles";
import {
  collapseWhitespace,
  formatBlockedChars,
  getDisallowedChars,
  stripDisallowedChars,
} from "../../utils/latinCharset";

const UserEditModal = ({
  show,
  handleEditClose,
  handleChange,
  formState,
  handleActivateUserChange,
  handleRoleChangeCheckbox,
  handleEdit,
}) => {
  const { t } = useTranslation(["translation", "register"]);
  const countryArr = useCountryArr();
  const roles = useSelector(selectCurrentRoles);

  // Which field last had disallowed characters removed, and which ones.
  const [blocked, setBlocked] = useState({ field: null, chars: [] });

  // Clear the hint whenever the modal is (re)opened.
  useEffect(() => {
    setBlocked({ field: null, chars: [] });
  }, [show]);

  // Strips non-Latin characters before handing the value up to the parent,
  // and records which characters were rejected for the hint.
  const handleFilteredChange = ({ target: { name, value } }) => {
    const cleaned = collapseWhitespace(stripDisallowedChars(value));
    const removed = getDisallowedChars(value);
    setBlocked(removed.length ? { field: name, chars: removed } : { field: null, chars: [] });
    return handleChange({ target: { name, value: cleaned } });
  };

  const charHint = (field) =>
    blocked.field === field && blocked.chars.length > 0 ? (
      <div className="small text-danger mt-1">
        {t("error.charSet", { ns: "register", chars: formatBlockedChars(blocked.chars) })}
      </div>
    ) : null;

  return (
    <Modal show={show} onHide={handleEditClose} backdrop="static" keyboard={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t("userList.editModal.title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3" controlId="formBasicUsername">
            <Form.Label>{t("userList.tableHeader.username")}</Form.Label>
            <Form.Control
              name="username"
              type="text"
              onChange={handleChange}
              value={formState.username}
              disabled
            />
            <Form.Text className="text-muted">{t("userList.editModal.usernameText")}</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicRegisterDate">
            <Form.Label>{t("userList.editModal.registerdAt")}</Form.Label>
            <Form.Control
              type="text"
              value={new Date(formState.register_date).toLocaleString()}
              disabled
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicLastActiveAt">
            <Form.Label>{t("userList.editModal.lastActive")}</Form.Label>
            <Form.Control
              type="text"
              value={new Date(formState.lastActiveAt).toLocaleString()}
              disabled
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicCheckbox">
            <Form.Check
              type="switch"
              id="custom-switch"
              label={t("userList.editModal.activatedText")}
              onChange={handleActivateUserChange}
              name="active"
              checked={formState.active}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicName">
            <Form.Label>{t("userList.tableHeader.name")}</Form.Label>
            <Form.Control
              name="name"
              type="text"
              onChange={handleFilteredChange}
              value={formState.name}
            />
            {charHint("name")}
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicSurname">
            <Form.Label>{t("userList.tableHeader.surname")}</Form.Label>
            <Form.Control
              name="surname"
              type="text"
              onChange={handleFilteredChange}
              value={formState.surname}
            />
            {charHint("surname")}
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label>{t("userList.tableHeader.email")}</Form.Label>
            <Form.Control
              name="email"
              type="text"
              onChange={handleFilteredChange}
              value={formState.email}
            />
            {charHint("email")}
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicPhone">
            <Form.Label>{t("userList.tableHeader.phone")}</Form.Label>
            <Form.Control
              name="phone"
              type="text"
              onChange={handleFilteredChange}
              value={formState.phone}
            />
            {charHint("phone")}
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicCompanyName">
            <Form.Label>{t("userList.tableHeader.company")}</Form.Label>
            <Form.Control
              name="company_name"
              type="text"
              onChange={handleFilteredChange}
              value={formState.company_name}
            />
            {charHint("company_name")}
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicCity">
            <Form.Label>{t("userList.tableHeader.city")}</Form.Label>
            <Form.Control
              name="city"
              type="text"
              onChange={handleFilteredChange}
              value={formState.city}
            />
            {charHint("city")}
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicAddress">
            <Form.Label>{t("userList.tableHeader.address")}</Form.Label>
            <Form.Control
              name="address"
              type="text"
              onChange={handleFilteredChange}
              value={formState.address}
            />
            {charHint("address")}
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicZipCode">
            <Form.Label>{t("userList.tableHeader.zipcode")}</Form.Label>
            <Form.Control
              name="zipcode"
              type="text"
              onChange={handleFilteredChange}
              value={formState.zipcode}
            />
            {charHint("zipcode")}
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicCountry">
            <Form.Label>{t("userList.tableHeader.country")}</Form.Label>
            <Form.Select
              name="country"
              value={formState.country}
              onChange={handleChange}
              className="register-form-control"
            >
              {!!countryArr?.length &&
                countryArr.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicRoles">
            <Form.Label>{t("userList.tableHeader.roles")}</Form.Label>
            <div key={`inline-checkbox`} className="mb-3">
              <Form.Check
                inline
                label="User"
                type={"checkbox"}
                id={`inline-checkbox-1`}
                onChange={handleRoleChangeCheckbox}
                name="User"
                checked={formState?.roles?.User}
                disabled={!isAllowedToSetUserRights(roles)}
              />
              {isAllowedToSetUserRights(roles) && (
                <Form.Check
                  inline
                  label="Approver"
                  type={"checkbox"}
                  id={`inline-checkbox-2`}
                  onChange={handleRoleChangeCheckbox}
                  name="Approver"
                  checked={formState?.roles?.Approver}
                  disabled={!isAllowedToSetApproverRights(roles)}
                />
              )}
              {isAllowedToSetApproverRights(roles) && (
                <Form.Check
                  inline
                  label="Admin"
                  type={"checkbox"}
                  id={`inline-checkbox-3`}
                  onChange={handleRoleChangeCheckbox}
                  name="Admin"
                  checked={formState?.roles?.Admin}
                  disabled={!isAllowedToSetAdminRights(roles)}
                />
              )}
              {isAllowedToSetAdminRights(roles) && (
                <Form.Check
                  inline
                  label="Superadmin"
                  type={"checkbox"}
                  id={`inline-checkbox-3`}
                  onChange={handleRoleChangeCheckbox}
                  name="Superadmin"
                  checked={formState?.roles?.Superadmin}
                />
              )}
            </div>
          </Form.Group>
          <Form.Group className="mb-3" controlId="formBasicNote">
            <Form.Label>{t("userList.editModal.note")}</Form.Label>
            <Form.Control
              name="note"
              as="textarea"
              onChange={handleFilteredChange}
              value={formState.note}
            />
            {charHint("note")}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleEditClose}>
          {t("userList.cancel")}
        </Button>
        <Button variant="primary" onClick={handleEdit}>
          {t("userList.save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserEditModal;
